import './SensorCard.css';

import React, { useEffect, useState } from 'react';
import { fullImagePath, generateClassName } from '../../b2b-common/helpers';
import { useIotFreworksContext } from '../pages/SensorsExplorerPage';
import JSONPretty from 'react-json-pretty';
import MoreButton from '../../b2b-common/MoreButton';
import { Delete, Edit, MoreVert } from '@mui/icons-material';
import EditSensorModal from '../forms/EditSensorModal';
import { deleteSensor, getAreaIndicatorSamplingBySourceId } from '../../../services/b2b-iotsampling-apiService';
import { useLoginContext } from '../../../App';
import { showNotification } from '../../b2b-common/Notification';
import { useContentContext } from '../../layout/Layout';
import GenericTabs from '../../b2b-common/GenericTabs';
import SamplingConfigsTable from './SampligConfigsTable';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

export function getSensorModelImage(framework, levelN_ItemId) {
    if (framework && framework.subLevels) {
        const levelN = framework.subLevels.find(levelN => levelN.levelN_ItemId === levelN_ItemId);
        if (levelN) {
            return fullImagePath(levelN.imagePath);
        }
    }
    return null;
}

export function getSensorModelShortName(framework, levelN_ItemId) {
    if (framework && framework.subLevels) {
        const levelN = framework.subLevels.find(levelN => levelN.levelN_ItemId === levelN_ItemId);
        if (levelN) {
            return levelN.levelN_ItemShortName;
        }
    }
    return null;
}

const SensorCard = ({ sensor, onDelete, onSave }) => {

    const { loginData } = useLoginContext();
    const { currentFramework } = useIotFreworksContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-sampling');

    const [currentSensor, setCurrentSensor] = useState(sensor);
    const [currentSensorImage, setCurrentSensorImage] = useState(null);
    const [showSensorModal, setShowSensorModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const [currentSamplingConfigs, setCurrentSamplingConfigs] = useState(null);

    const getAreaIndicatorsUsingSensor = async (sourceConfigId) => {
        try {
            const samplingConfigs = await getAreaIndicatorSamplingBySourceId(loginData.accessToken, sourceConfigId, true);
            setCurrentSamplingConfigs(samplingConfigs);
        } catch (error) {
            console.error('getAreaIndicatorsUsingSensor error:', error);
            showNotification(setNotification, t('Error fetching area indicators related to sensor: ') + error.message, 'error', 5000);
        }
    }

    useEffect(() => {
        if (sensor && (sensor.sourceConfigId !== currentSensor.sourceConfigId || !currentSamplingConfigs)) {
            setCurrentSensor(sensor);
            if (!currentSamplingConfigs)
                getAreaIndicatorsUsingSensor(sensor.sourceConfigId);
        }
    }, [sensor]);

    useEffect(() => {
        if (currentFramework && sensor) {
            setCurrentSensorImage(getSensorModelImage(currentFramework, sensor.levelN_ItemId));
        }
    }, [currentFramework, sensor]);

    const handleEditSensor = () => {
        // console.log('Edit sensor clicked');
        setShowSensorModal(true);
    }

    const handleDeleteSensor = async () => {
        try {
            await deleteSensor(loginData.accessToken, loginData.loggedOrganizationUuid, currentSensor.sourceConfigId);
            setShowDeleteConfirmation(false);
            showNotification(setNotification, t('Sensor deleted successfully'), 'success', 3000);
            onDelete && onDelete(currentSensor.sourceConfigId);
        } catch (error) {
            console.error('deleteSensor error:', error);
            showNotification(setNotification, t('Error deleting sensor: ') + error.message, 'error', 3000);
        }
    }

    const handleDeleteSamplingConfig = (areaIndicatorId) => {
        getAreaIndicatorsUsingSensor(currentSensor.sourceConfigId);
    }

    const tabsConfig = [
        {
            label: t('Sensor'),
            icon: null,
            content: (
                <div className='sensor-card-info'>
                    <div className='sensor-card-name'>{currentSensor.sourceName}</div>
                    <div className='sensor-card-description'>{currentSensor.sourceDescription}</div>
                </div>
            )
        },
        {
            label: t('Configuration'),
            icon: null,
            content: (
                <div className='sensor-card-configuration'>
                    <JSONPretty id={'json-pretty-' + currentSensor.sourceConfigId} data={currentSensor.sourceConfiguration}
                        className='sensor-card-json-config-pretty' />
                </div>
            )
        },
        {
            label: t('Area indicators'),
            icon: null,
            content: (
                <SamplingConfigsTable samplingConfigs={currentSamplingConfigs} onDelete={handleDeleteSamplingConfig} />
            ),
            showTab: currentSamplingConfigs && currentSamplingConfigs.length > 0,
        },
    ];

    return (
        currentSensor && currentSensorImage && currentFramework && (
            <div className={generateClassName('sensor-card', '-container')}>
                <div className='sensor-card-image'>
                    <img src={currentSensorImage} alt={currentSensor.sourceName} />
                    <div className='sensor-card-sensor-short-name'>{getSensorModelShortName(currentFramework, currentSensor.levelN_ItemId)}</div>
                </div>
                <GenericTabs
                    tabsConfig={tabsConfig}
                    className='sensor-card-info'
                />
                <MoreButton icon={<MoreVert />} className={'sensor-card-more-button jb01-more-button'}
                    buttons={[
                        {
                            label: t('Edit'),
                            icon: <Edit />,
                            rolesAllowed: ['ADMIN'],
                            onClick: handleEditSensor,
                        },
                        {
                            label: t('Delete'),
                            icon: <Delete />,
                            rolesAllowed: ['ADMIN'],
                            onClick: () => setShowDeleteConfirmation(true),
                        }
                    ]}
                    dropPositionAlign='right'
                    rolesAllowed={['ADMIN']}
                />
                <EditSensorModal sensor={currentSensor} open={showSensorModal}
                    onCancel={() => setShowSensorModal(false)}
                    onSave={() => { onSave && onSave(currentSensor); setShowSensorModal(false); }}
                />
                <ConfirmationDialog
                    title={t('Sensor Delete Confirmation Request')}
                    mainText={t('Are you sure you want to delete the selected sensor?')}
                    open={showDeleteConfirmation}
                    onClose={() => setShowDeleteConfirmation(false)}
                    onConfirm={handleDeleteSensor}
                />
            </div>
        )
    );
};

export default SensorCard;